import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { OptimizedImage } from '@/components/OptimizedImage';
import { CardLogo } from '@/components/CardLogo';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Star, 
  Sparkles, 
  Plane, 
  Coins, 
  Ban, 
  TrendingDown, 
  CreditCard,
  Globe,
  Hotel,
  Car,
  ShoppingBag,
  Lock,
  FileText,
  Users,
  Clock,
  ArrowRight
} from 'lucide-react';

// TYPE DEFINITIONS
export interface HeroCardData {
  name: string;
  bank: string;
  imageUrl: string | null;
  subtitle?: string;
  effectiveRate: number;
  annualFee: number;
  creditLimit: number;
  creditLimitMin?: number | null;
  creditLimitMax?: number | null;
  cardType?: string | null;
  interestFreePeriod: number;
  aprNominal?: number | null;
  network?: string | null;
  currentAccountIncluded?: boolean;
  isPrepaid?: boolean;
  
  // Feature flags for smart tag generation
  travelInsurance?: boolean;
  feesForPurchasesAbroad?: number;
  hasCashback?: boolean;
  hasLounge?: boolean;
  hasCarRentalInsurance?: boolean;
  hasDiscounts?: boolean;
  isMembershipRequired?: boolean;
}

interface HeroSplitScreenProps {
  card: HeroCardData;
  cardDetailUrl: string;
  isPromoted?: boolean;
  onCTAClick?: () => void;
  ctaHref?: string;
  onLearnMoreClick?: () => void;
  onImageClick?: () => void;
  onTitleClick?: () => void;
  isLoading?: boolean;
  hasAffiliateLink?: boolean;
  displayConfigChips?: Array<{label: string; tooltip?: string; priority?: number}>;
  isGerman?: boolean;
  isCompact?: boolean;
  /** Only the first card above the fold should load its image eagerly. */
  imagePriority?: boolean;
}

export function HeroSplitScreen({ card, cardDetailUrl, isPromoted = false, onCTAClick, ctaHref, onLearnMoreClick, onImageClick, onTitleClick, isLoading = false, hasAffiliateLink = true, displayConfigChips, isGerman = false, isCompact = false, imagePriority = false }: HeroSplitScreenProps) {
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  // Card category: only real credit cards (revolving) may show interest metrics
  const category = (card.cardType?.toLowerCase() || (card.isPrepaid ? 'prepaid' : 'credit')) as
    | 'credit'
    | 'charge'
    | 'debit'
    | 'prepaid';
  const noCreditMetrics = category !== 'credit';
  const categoryLabel =
    category === 'prepaid'
      ? 'Prepaidkarte'
      : category === 'debit'
        ? 'Debitkarte'
        : category === 'charge'
          ? 'Charge-Karte'
          : 'Kreditkarte';
  const categoryTooltip =
    category === 'prepaid'
      ? 'Prepaidkarten haben keinen Kreditrahmen und damit keine Sollzinsen.'
      : category === 'debit'
        ? 'Debitkarten belasten das Girokonto direkt – kein Kreditrahmen, keine Sollzinsen.'
        : 'Charge-Karten werden monatlich vollständig abgebucht – keine Teilzahlung, keine Sollzinsen.';

  // One earned, data-backed highlight (never an editorial claim)
  const heroHighlight =
    card.annualFee === 0
      ? '0 € Jahresgebühr'
      : card.feesForPurchasesAbroad === 0
        ? 'Keine Fremdwährungsgebühr'
        : card.travelInsurance
          ? 'Reiseversicherung inklusive'
          : null;


  // SMART TAG GENERATION SYSTEM
  interface FeatureTag {
    icon: any;
    label: string;
    color: string;
    tooltip: string;
    priority: number;
  }

  const allFeatures: FeatureTag[] = [];

  // 💳 FINANCIAL & PRACTICAL TAGS
  
  if (card.annualFee === 0) {
    allFeatures.push({
      icon: Ban,
      label: 'Gratis Jahresgebühr',
      color: 'text-emerald-600',
      tooltip: 'Keine Jahresgebühr – dauerhaft kostenlos',
      priority: 10,
    });
  }

  if (card.creditLimit > 150000) {
    allFeatures.push({
      icon: CreditCard,
      label: 'Hohe Kreditlinie',
      color: 'text-blue-600',
      tooltip: `Bis zu ${card.creditLimit.toLocaleString('de-DE')} € Kreditrahmen für größere Einkäufe`,
      priority: 7,
    });
  }

  if (card.interestFreePeriod >= 45) {
    allFeatures.push({
      icon: Clock,
      label: `${card.interestFreePeriod} Tage zinsfrei`,
      color: 'text-purple-600',
      tooltip: `Bis zu ${card.interestFreePeriod} Tage keine Zinsen bei vollständiger Rückzahlung`,
      priority: 8,
    });
  }

  if (card.effectiveRate > 0 && card.effectiveRate < 18) {
    allFeatures.push({
      icon: TrendingDown,
      label: 'Niedriger Zinssatz',
      color: 'text-green-600',
      tooltip: `Nur ${card.effectiveRate}% effektiver Jahreszins – niedriger als der Durchschnitt`,
      priority: 7,
    });
  }

  if (card.isMembershipRequired) {
    allFeatures.push({
      icon: Users,
      label: 'Für Mitglieder',
      color: 'text-indigo-600',
      tooltip: 'Erfordert Mitgliedschaft in Gewerkschaft oder Bank',
      priority: 5,
    });
  }

  // ✈️ TRAVEL & LIFESTYLE TAGS
  
  if (card.travelInsurance) {
    allFeatures.push({
      icon: Plane,
      label: 'Reiseversicherung enthalten',
      color: 'text-blue-600',
      tooltip: 'Reiseversicherung ist im Kartenpaket enthalten',
      priority: 9,
    });
  }

  if (card.feesForPurchasesAbroad === 0) {
    allFeatures.push({
      icon: Globe,
      label: 'Kein Auslandseinsatzentgelt',
      color: 'text-cyan-600',
      tooltip: 'Keine Gebühren bei Zahlungen im Ausland',
      priority: 9,
    });
  }

  if (card.hasLounge) {
    allFeatures.push({
      icon: Hotel,
      label: 'Lounge-Zugang',
      color: 'text-purple-600',
      tooltip: 'Kostenloser Zugang zu über 1.000 Flughafen-Lounges weltweit',
      priority: 8,
    });
  }

  if (card.hasCarRentalInsurance) {
    allFeatures.push({
      icon: Car,
      label: 'Mietwagen-Versicherung',
      color: 'text-orange-600',
      tooltip: 'Versicherung für Selbstbeteiligung bei Mietwagen inklusive',
      priority: 6,
    });
  }

  // 💰 REWARDS & BENEFITS TAGS
  
  if (card.hasCashback) {
    allFeatures.push({
      icon: Coins,
      label: 'Cashback / Bonusprogramm',
      color: 'text-amber-600',
      tooltip: 'Erhalten Sie Cashback oder Bonuspunkte bei Einkäufen',
      priority: 10,
    });
  }

  if (card.hasDiscounts) {
    allFeatures.push({
      icon: ShoppingBag,
      label: 'Rabatte',
      color: 'text-pink-600',
      tooltip: 'Profitieren Sie von Rabatten z. B. bei Reisen oder Mietwagen',
      priority: 6,
    });
  }

  // 🔒 SECURITY / USABILITY TAGS
  
  allFeatures.push({
    icon: Lock,
    label: 'Sicheres Online-Shopping',
    color: 'text-slate-600',
    tooltip: 'Käuferschutz und Betrugsschutz bei allen Online-Einkäufen',
    priority: 4,
  });

  allFeatures.push({
    icon: FileText,
    label: 'Einfacher Antrag',
    color: 'text-gray-600',
    tooltip: 'Einfacher und schneller Antragsprozess',
    priority: 5,
  });

  // Helper function to map chip labels to icons for German cards
  const getIconForChip = (label: string) => {
    const lower = label.toLowerCase();
    if (lower.includes('gratis') || lower.includes('0 €')) return Ban;
    if (lower.includes('ausland') || lower.includes('0 %')) return Globe;
    if (lower.includes('reise')) return Plane;
    if (lower.includes('kontaktlos') || lower.includes('nfc')) return CreditCard;
    if (lower.includes('girokonto')) return Users;
    if (lower.includes('rabatt')) return ShoppingBag;
    if (lower.includes('hotline')) return Clock;
    if (lower.includes('abheben')) return Coins;
    if (lower.includes('prepaid')) return CreditCard;
    if (lower.includes('teilzahlung')) return FileText;
    return Sparkles;
  };

  // Use display_config chips for German cards, otherwise use feature detection
  const sortedFeatures = isGerman && displayConfigChips && displayConfigChips.length > 0
    ? displayConfigChips.map(chip => ({
        icon: getIconForChip(chip.label),
        label: chip.label,
        color: 'text-blue-600',
        tooltip: chip.tooltip || chip.label,
        priority: chip.priority || 5
      }))
    : allFeatures.sort((a, b) => b.priority - a.priority);
  
  const visibleFeatures = showAllFeatures ? sortedFeatures : sortedFeatures.slice(0, 4);
  const hiddenCount = sortedFeatures.length - 4;

  // Generate dynamic subtitle from top features
  const subtitle = card.subtitle || 'Kreditkarte mit Reiseversicherung und Cashback auf alle Einkäufe';
  
  // Generate key benefits line (optional)
  const keyBenefits: string[] = [];
  if (card.annualFee === 0) keyBenefits.push('Keine Jahresgebühr');
  if (card.feesForPurchasesAbroad === 0) keyBenefits.push('0% Auslandseinsatzentgelt');
  if (card.creditLimit > 0) keyBenefits.push(`Bis zu ${card.creditLimit.toLocaleString('de-DE')} € Kredit`);
  const keyBenefitsText = keyBenefits.join(' • ');

  // Helper function to determine third slot content
  const getThirdSlotContent = () => {
    // CASE 1: Not a revolving credit card
    if (noCreditMetrics) {
      return {
        label: 'Kartentyp',
        value: categoryLabel,
        tooltip: categoryTooltip
      };
    }
    
    // CASE 2: Valid Credit Limit Range
    if ((card.creditLimitMin && card.creditLimitMin > 0) || 
        (card.creditLimitMax && card.creditLimitMax > 0)) {
      const min = card.creditLimitMin || 0;
      const max = card.creditLimitMax || card.creditLimitMin || 0;
      
      let value: string;
      if (min > 0 && max > 0 && min !== max) {
        value = `${min.toLocaleString('de-DE')} – ${max.toLocaleString('de-DE')} €`;
      } else if (max > 0) {
        value = `${max.toLocaleString('de-DE')} €`;
      } else {
        value = `${min.toLocaleString('de-DE')} €`;
      }
      
      return {
        label: 'Kreditrahmen',
        value,
        tooltip: 'Der Kreditrahmen zeigt, wie viel Sie maximal mit dieser Karte ausgeben können.'
      };
    }
    
    // CASE 3: Interest-Free Days Available
    if (card.interestFreePeriod > 0) {
      return {
        label: 'Zinsfreie Tage',
        value: `${card.interestFreePeriod} Tage`,
        tooltip: 'Innerhalb dieses Zeitraums fallen keine Zinsen auf Ihre Einkäufe an.'
      };
    }
    
    // CASE 4: Fallback - Monthly Cost Example
    const principal = 1500;
    const months = 12;
    const monthlyRate = card.effectiveRate / 100 / 12;
    const totalInterest = principal * monthlyRate * months;
    const totalCost = Math.round(principal + totalInterest);
    
    return {
      label: 'Beispielkosten gesamt',
      value: `${totalCost.toLocaleString('de-DE')} €`,
      tooltip: `Beispielrechnung: 1.500 € über 12 Monate – dient dem Vergleich der Kreditkartenkosten.`
    };
  };

  const thirdSlot = getThirdSlotContent();
  
  // Helper function to determine 4th slot (Zinsfrei replacement)
  const getFourthSlotContent = () => {
    // CASE 1: Not a revolving credit card (no interest concept)
    if (noCreditMetrics) {
      return {
        label: 'Kartentyp',
        value: categoryLabel,
        tooltip: categoryTooltip
      };
    }

    // CASE 2: Has Interest-Free Days (show actual value)
    if (card.interestFreePeriod > 0) {
      return {
        label: 'Zinsfreie Tage',
        value: `${card.interestFreePeriod} Tage`,
        tooltip: 'Einkäufe bleiben zinsfrei bis zur Rechnungsstellung.'
      };
    }
    
    // CASE 3: Show Sollzins (Nominal APR) if available
    if (card.aprNominal && card.aprNominal > 0) {
      return {
        label: 'Sollzins',
        value: `${card.aprNominal.toFixed(2).replace('.', ',')} %`,
        tooltip: 'Effektiver Jahreszins bei Nutzung des Kreditrahmens.'
      };
    }
    
    // CASE 4: Show "0 € Gebühren" if annual fee is 0
    if (card.annualFee === 0) {
      return {
        label: 'Gebühren',
        value: '0 €',
        tooltip: 'Keine Jahres- oder Kontoführungsgebühren.'
      };
    }
    
    // CASE 5: Fallback - Generic "Kreditkarte" label
    return {
      label: 'Kartentyp',
      value: 'Kreditkarte',
      tooltip: 'Echte Kreditkarte mit flexibler Rückzahlung.'
    };
  };

  const fourthSlot = getFourthSlotContent();

  return (
    <div className={`flex flex-col lg:flex-row ${isCompact ? 'min-h-[320px]' : 'min-h-[400px]'}`}>
      {/* LEFT PANEL: Card Showcase */}
      <div className={`w-full lg:w-1/2 bg-gradient-to-br from-slate-100 to-gray-100 flex items-center justify-center ${isCompact ? 'p-6 lg:p-8' : 'p-8 lg:p-12'} relative overflow-hidden group`}>
        {/* Subtle dot pattern background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(0 0 0 / 0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="relative w-full flex flex-col items-center">
          {/* Card Image */}
          {onImageClick ? (
            <a
              href={cardDetailUrl}
              onClick={(e) => {
                e.preventDefault();
                onImageClick();
              }}
              className="relative mb-6 block focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-2xl transition-all"
              aria-label={`Mehr über ${card.name} erfahren`}
            >
              <div className={`${isCompact ? 'w-56' : 'w-72'} rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-gray-100 to-gray-200 transition-all duration-500 group-hover:scale-105 group-hover:shadow-3xl`} style={{ aspectRatio: '3/2', height: isCompact ? '144px' : '192px' }}>
                {!isLoading ? (
                  <CardLogo 
                    logoUrl={card.imageUrl}
                    bankName={card.bank}
                    className="block w-full h-full object-cover"
                    loading={imagePriority ? "eager" : "lazy"}
                    priority={imagePriority}
                    width={isCompact ? 224 : 288}
                    height={isCompact ? 144 : 192}
                  />
                ) : (
                  <Skeleton className="w-full aspect-[3/2]" />
                )}
              </div>
              {/* Subtle glow effect on hover */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-400/0 to-emerald-400/0 group-hover:from-blue-400/10 group-hover:to-emerald-400/10 transition-all duration-500 pointer-events-none" />
            </a>
          ) : (
            <div className="relative mb-6 rounded-2xl">
              <div className={`${isCompact ? 'w-56' : 'w-72'} rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-gray-100 to-gray-200`} style={{ aspectRatio: '3/2', height: isCompact ? '144px' : '192px' }}>
                {!isLoading ? (
                  <CardLogo 
                    logoUrl={card.imageUrl}
                    bankName={card.bank}
                    className="block w-full h-full object-cover"
                    loading={imagePriority ? "eager" : "lazy"}
                    priority={imagePriority}
                    width={isCompact ? 224 : 288}
                    height={isCompact ? 144 : 192}
                  />
                ) : (
                  <Skeleton className="w-full aspect-[3/2]" />
                )}
              </div>
            </div>
          )}
          
          {/* Feature Chips - Below card, centered */}
          <TooltipProvider>
            <div className="flex flex-wrap gap-2 justify-center max-w-md">
              {!isLoading ? (
                <>
                  {/* Show top 4 features (or all if expanded) */}
                  {visibleFeatures.map((feature, index) => (
                    <Tooltip key={index} delayDuration={200}>
                      <TooltipTrigger asChild>
                        <div className={`inline-flex items-center gap-2 ${isCompact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} bg-white border border-gray-300 rounded-full hover:border-gray-400 hover:shadow-xs transition-all cursor-help`}>
                          <feature.icon className={`${isCompact ? 'w-3 h-3' : 'w-4 h-4'} ${feature.color}`} />
                          <span className="text-gray-700">{feature.label}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>{feature.tooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                  
                  {/* "+X flere fordeler" expandable button */}
                  {hiddenCount > 0 && (
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => setShowAllFeatures(!showAllFeatures)}
                          className={`inline-flex items-center gap-2 ${isCompact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} bg-blue-50 border border-blue-300 rounded-full hover:bg-blue-100 hover:border-blue-400 hover:shadow-xs transition-all cursor-pointer`}
                        >
                          <Sparkles className={`${isCompact ? 'w-3 h-3' : 'w-4 h-4'} text-blue-600`} />
                          <span className="text-blue-700">
                            {showAllFeatures ? 'Weniger anzeigen' : `+${hiddenCount} ${hiddenCount === 1 ? 'weiterer Vorteil' : 'weitere Vorteile'}`}
                          </span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>{showAllFeatures ? 'Zusätzliche Vorteile ausblenden' : `Klicken Sie, um alle ${sortedFeatures.length} Vorteile zu sehen`}</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </>
              ) : (
                <>
                  {/* Loading skeleton for feature tags */}
                  <Skeleton className="h-8 w-40 rounded-full" />
                  <Skeleton className="h-8 w-36 rounded-full" />
                  <Skeleton className="h-8 w-44 rounded-full" />
                  <Skeleton className="h-8 w-32 rounded-full" />
                </>
              )}
            </div>
          </TooltipProvider>
        </div>
      </div>
      
      {/* RIGHT PANEL: Card Information */}
      <div className={`w-full lg:w-1/2 bg-white flex items-center ${isCompact ? 'p-6 lg:p-8' : 'p-8 lg:p-12'}`}>
        <div className="max-w-md w-full mx-auto">
            {/* Factual badges: card type + at most one data-backed highlight */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="cursor-help">{categoryLabel}</Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-sm max-w-xs">{categoryTooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {heroHighlight && (
                <Badge className="bg-blue-50 text-blue-800 border-blue-200">{heroHighlight}</Badge>
              )}
            </div>

          
          {/* Card Title */}
          {!isLoading ? (
            <>
              <h3 className={`text-gray-900 ${isCompact ? 'text-xl lg:text-2xl mb-2' : 'text-2xl lg:text-3xl mb-3'}`}>
                {onTitleClick ? (
                  <a
                    href={cardDetailUrl}
                    onClick={(e) => {
                      e.preventDefault();
                      onTitleClick();
                    }}
                    className="hover:underline focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-sm transition-all"
                  >
                    {card.name}
                  </a>
                ) : (
                  <span>{card.name}</span>
                )}
              </h3>
              
              {/* Dynamic Subtitle */}
              <p className={`text-gray-600 ${isCompact ? 'mb-1 text-xs' : 'mb-2 text-sm'}`}>
                {subtitle}
              </p>
              
              {/* Key Benefits Line (optional) */}
              {keyBenefitsText && (
                <p className={`text-gray-500 ${isCompact ? 'text-[11px] mb-4' : 'text-xs mb-6 lg:mb-8'}`}>
                  {keyBenefitsText}
                </p>
              )}
            </>
          ) : (
            <div className={`space-y-3 ${isCompact ? 'mb-4' : 'mb-6 lg:mb-8'}`}>
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-5 w-80" />
              <Skeleton className="h-4 w-56" />
            </div>
          )}
          
          {/* Metrics Grid - 2x2 grid of key stats */}
          <div className={`grid grid-cols-2 ${isCompact ? 'gap-2 lg:gap-3 mb-4' : 'gap-3 lg:gap-4 mb-6 lg:mb-8'}`}>
            {!isLoading ? (
              <>
                {/* Box 1: APR only for real credit cards, otherwise Kartennetz */}
                {noCreditMetrics ? (
                  <div className={`border border-gray-200 rounded-lg ${isCompact ? 'p-2 lg:p-3' : 'p-3 lg:p-4'}`}>
                    <div className={`${isCompact ? 'text-[11px]' : 'text-xs lg:text-sm'} text-gray-600 mb-1`}>Kartennetz</div>
                    <div className={`${isCompact ? 'text-base lg:text-lg' : 'text-xl lg:text-2xl'} text-gray-900`}>{card.network || 'Mastercard'}</div>
                  </div>
                ) : (
                  <TooltipProvider>
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger asChild>
                        <div className={`border border-gray-200 rounded-lg ${isCompact ? 'p-2 lg:p-3' : 'p-3 lg:p-4'} cursor-help`}>
                          <div className={`${isCompact ? 'text-[11px]' : 'text-xs lg:text-sm'} text-gray-600 mb-1`}>Eff. Zinssatz</div>
                          <div className={`${isCompact ? 'text-base lg:text-lg' : 'text-xl lg:text-2xl'} text-gray-900`}>
                            {`${card.effectiveRate}%`}
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Effektiver Jahreszins bei Nutzung des Kreditrahmens.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                
                {/* Box 2: Annual Fee */}
                <div className={`border border-gray-200 rounded-lg ${isCompact ? 'p-2 lg:p-3' : 'p-3 lg:p-4'}`}>
                  <div className={`${isCompact ? 'text-[11px]' : 'text-xs lg:text-sm'} text-gray-600 mb-1`}>Jahresgebühr</div>
                  <div className={`${isCompact ? 'text-base lg:text-lg' : 'text-xl lg:text-2xl'} text-gray-900`}>{card.annualFee === 0 ? 'Gratis' : `${card.annualFee.toLocaleString('de-DE')} €`}</div>
                </div>
                
                {/* Box 3: Kartentyp for non-credit cards, otherwise limit/interest-free days */}
                <TooltipProvider>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <div className={`border border-gray-200 rounded-lg ${isCompact ? 'p-2 lg:p-3' : 'p-3 lg:p-4'} cursor-help`}>
                        <div className={`${isCompact ? 'text-[11px]' : 'text-xs lg:text-sm'} text-gray-600 mb-1`}>{thirdSlot.label}</div>
                        <div className={`${isCompact ? 'text-base lg:text-lg' : 'text-xl lg:text-2xl'} text-gray-900`}>{thirdSlot.value}</div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>{thirdSlot.tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Box 4: Mit Girokonto for non-credit cards, otherwise other slot */}
                {noCreditMetrics ? (
                  <div className={`border border-gray-200 rounded-lg ${isCompact ? 'p-2 lg:p-3' : 'p-3 lg:p-4'}`}>
                    <div className={`${isCompact ? 'text-[11px]' : 'text-xs lg:text-sm'} text-gray-600 mb-1`}>Mit Girokonto</div>
                    <div className={`${isCompact ? 'text-base lg:text-lg' : 'text-xl lg:text-2xl'} text-gray-900`}>{card.currentAccountIncluded ? 'Ja' : 'Nein'}</div>
                  </div>
                ) : (
                  <TooltipProvider>
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger asChild>
                        <div className={`border border-gray-200 rounded-lg ${isCompact ? 'p-2 lg:p-3' : 'p-3 lg:p-4'} cursor-help`}>
                          <div className={`${isCompact ? 'text-[11px]' : 'text-xs lg:text-sm'} text-gray-600 mb-1`}>{fourthSlot.label}</div>
                          <div className={`${isCompact ? 'text-base lg:text-lg' : 'text-xl lg:text-2xl'} text-gray-900`}>{fourthSlot.value}</div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>{fourthSlot.tooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </>
            ) : (
              <>
                <Skeleton className="border border-gray-200 rounded-lg p-3 lg:p-4 h-20" />
                <Skeleton className="border border-gray-200 rounded-lg p-3 lg:p-4 h-20" />
                <Skeleton className="border border-gray-200 rounded-lg p-3 lg:p-4 h-20" />
                <Skeleton className="border border-gray-200 rounded-lg p-3 lg:p-4 h-20" />
              </>
            )}
          </div>
          
          {/* CTA Button - Only show if affiliate link exists */}
          <div>
            {!isLoading ? (
              <>
                {hasAffiliateLink && (onCTAClick || ctaHref) && (
                  <>
                    <div className="flex flex-col lg:flex-row items-center gap-4">
                      {ctaHref ? (
                        <a
                          href={ctaHref}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          onClick={(e) => {
                            if (onCTAClick) {
                              e.preventDefault();
                              onCTAClick();
                            }
                          }}
                          className={`inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${
                            isPromoted 
                              ? `w-full lg:w-auto bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all duration-300 group ${isCompact ? 'px-4 py-2 text-base' : 'px-6 py-3 text-lg'} text-white`
                              : `w-full lg:w-auto bg-primary text-primary-foreground hover:bg-primary/90 ${isCompact ? 'h-9 px-6 text-sm' : 'h-11 px-8'}`
                          }`}
                        >
                          <span className={isPromoted ? "group-hover:scale-105 inline-block transition-transform duration-300" : ""}>
                            Jetzt beantragen
                          </span>
                          <ArrowRight className={`ml-2 ${isCompact ? 'h-4 w-4' : 'h-5 w-5'}`} />
                        </a>
                      ) : (
                        <Button 
                          size={isCompact ? "default" : "lg"}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onCTAClick) onCTAClick();
                          }}
                          className={isPromoted 
                            ? `w-full lg:w-auto bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all duration-300 group ${isCompact ? 'px-4 py-2 text-base' : 'px-6 py-3 text-lg'}`
                            : "w-full lg:w-auto"
                          }
                        >
                          <span className={isPromoted ? "group-hover:scale-105 inline-block transition-transform duration-300" : ""}>
                            Jetzt beantragen
                          </span>
                          <ArrowRight className={`ml-2 ${isCompact ? 'h-4 w-4' : 'h-5 w-5'}`} />
                        </Button>
                      )}
                      
                      {onLearnMoreClick && (
                        <a
                          href={cardDetailUrl}
                          onClick={(e) => {
                            e.preventDefault();
                            onLearnMoreClick();
                          }}
                          className="text-gray-600 hover:text-gray-800 underline text-sm transition-colors"
                        >
                          Mehr erfahren
                        </a>
                      )}
                    </div>
                    
                     {/* "Anzeige" indicator for promoted cards */}
                     {isPromoted && (
                       <div className="text-xs text-gray-500 mt-2 text-center lg:text-left">Anzeige</div>
                     )}
                  </>
                )}
              </>
            ) : (
              <Skeleton className="h-12 w-40 rounded-lg" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
